import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { SpeakingLevelBadge } from "../UI/SpeakingLevelBadge.tsx";
import { BasicTable } from "../UI/Table/BasicTable.tsx";
import {
  EllipsisHorizontalIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline/index.js";
import { DropDown } from "../UI/DropDown/DropDown.tsx";
import { DropDownItem } from "../UI/DropDown/DropDownItem.tsx";
import { useCopyToClipboard } from "../UI/Feedback/useCopyToClipboard.ts";

type ProgressCheckPdfRow = {
  level: string;
  pdfUrl: string;
  userGroupId: string;
  userGroup?: { id: string; slug: string; name: string } | null;
};

type Props = {
  items: ProgressCheckPdfRow[];
  onEdit: (row: ProgressCheckPdfRow) => void;
};

export function ProgressCheckPdfTable({ items, onEdit }: Props) {
  const copyToClipboard = useCopyToClipboard();
  const columnHelper = createColumnHelper<ProgressCheckPdfRow>();
  const columns = [
    columnHelper.accessor("level", {
      header: "Level",
      cell: (info) => <SpeakingLevelBadge level={info.getValue()} />,
    }),
    columnHelper.accessor("pdfUrl", {
      header: "Pdf Url",
      meta: { className: "hidden md:table-cell" },
      cell: (info) => (
        <div
          onClick={() => {
            copyToClipboard(info.getValue());
          }}
          className={
            "max-w-[240px] overflow-hidden overflow-ellipsis cursor-pointer"
          }
        >
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.display({
      id: "userGroupSlug",
      header: "User group",
      cell: (v) => v.row.original.userGroup?.slug ?? "-",
    }),
    columnHelper.display({
      id: "actions",
      cell: (v) => (
        <div className={"flex justify-end"}>
          <DropDown
            activator={
              <EllipsisHorizontalIcon
                className={"text-slate-800 w-5 h-5 group-hover:text-white"}
              />
            }
          >
            <DropDownItem onClick={() => onEdit(v.row.original)}>
              <PencilSquareIcon className={"w-5 h-5 mr-2"} />
              Edit
            </DropDownItem>
          </DropDown>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    columns,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
  });

  return <BasicTable table={table} />;
}
